import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

const VIBE_BACKEND = import.meta.env.VITE_VIBE_BACKEND_URL || 'http://localhost:8001';

// ── Helpers ───────────────────────────────────────────────────────────────────
const trunc = (str, n = 80) => str && str.length > n ? str.slice(0, n) + '…' : (str || '');

// ── Typing animation for prompt display ──────────────────────────────────────
const TypingText = ({ text, speed = 8, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);

  const onDoneRef = useRef(onDone);
  const speedRef  = useRef(speed);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => { speedRef.current  = speed;  }, [speed]);

  useEffect(() => {
    if (!text) return;
    idx.current = 0;
    setDisplayed('');
    const iv = setInterval(() => {
      idx.current += 3;
      setDisplayed(text.slice(0, idx.current));
      if (idx.current >= text.length) {
        clearInterval(iv);
        onDoneRef.current?.();
      }
    }, speedRef.current);
    return () => clearInterval(iv);
  }, [text]);

  return (
    <span>
      {displayed}
      {displayed.length < (text?.length || 0) && (
        <span className="inline-block w-0.5 h-4 bg-[#ff4500] ml-0.5 animate-pulse" />
      )}
    </span>
  );
};

// ── File tab component ────────────────────────────────────────────────────────
const FileTab = ({ file, active, onClick }) => {
  const ext = file.name.split('.').pop();
  const colors = { jsx: '#38bdf8', html: '#fb923c', css: '#a78bfa', js: '#fbbf24', py: '#4ade80' };
  const color = colors[ext] || '#7b7a88';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-xs border-b-2 transition-all flex-shrink-0 ${
        active ? 'border-[#ff4500] text-white bg-[#17171c]' : 'border-transparent text-[#7b7a88] hover:text-white hover:bg-[#17171c]/50'
      }`}
      style={{ fontFamily: 'DM Mono, monospace' }}>
      <span style={{ color }}>{ext.toUpperCase()}</span>
      <span>{file.name}</span>
    </button>
  );
};

// ── Code syntax highlighter (lightweight) ────────────────────────────────────
const CodeBlock = ({ code, language }) => {
  const highlighted = React.useMemo(() => {
    if (!code) return '';
    return code
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // strings
      .replace(/(["'`])((?:(?!\1)[^\\]|\\.)*)(\1)/g, '<span style="color:#4ade80">$1$2$3</span>')
      // keywords
      .replace(/\b(const|let|var|function|return|import|export|default|from|if|else|for|while|class|extends|new|this|typeof|async|await|useState|useEffect|useRef|useCallback)\b/g,
        '<span style="color:#a78bfa">$1</span>')
      // JSX tags
      .replace(/(&lt;\/?[A-Z][a-zA-Z]*)/g, '<span style="color:#38bdf8">$1</span>')
      // comments
      .replace(/(\/\/[^\n]*)/g, '<span style="color:#3a3a48">$1</span>')
      // numbers
      .replace(/\b(\d+)\b/g, '<span style="color:#fb923c">$1</span>');
  }, [code]);

  return (
    <pre className="text-xs leading-relaxed overflow-auto h-full p-4"
      style={{ fontFamily: 'DM Mono, monospace', color: '#c8c7d4' }}
      dangerouslySetInnerHTML={{ __html: highlighted }} />
  );
};

// ── Live preview renderer ─────────────────────────────────────────────────────
const LivePreviewFrame = ({ files, deviceMode }) => {
  const iframeRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);

  const htmlFile = files?.find(f => f.name === 'index.html');

  // Force re-mount iframe when content changes so srcdoc takes effect
  useEffect(() => {
  if (htmlFile) setIframeKey(k => k + 1);
}, [htmlFile]);

  const deviceStyles = {
    desktop: 'w-full h-full',
    tablet:  'w-[768px] h-[1024px] mx-auto',
    mobile:  'w-[375px] h-[812px] mx-auto',
  };

  if (!htmlFile) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d0d12]">
        <div className="text-center space-y-3">
          <div className="text-5xl">🖥️</div>
          <p className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>
            Preview will appear here after generation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden bg-[#0d0d12] flex items-start justify-center p-4">
      <div className={`${deviceStyles[deviceMode]} bg-white rounded-lg overflow-hidden shadow-2xl transition-all duration-300`}
        style={{ maxHeight: '100%' }}>
        <iframe
          key={iframeKey}
          ref={iframeRef}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-top-navigation-by-user-activation"
          srcDoc={htmlFile.content}
          title="App Preview"
        />
      </div>
    </div>
  );
};

// ── Chat message ──────────────────────────────────────────────────────────────
const ChatMessage = ({ msg }) => (
  <div className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
      ${msg.role === 'user' ? 'bg-[#ff4500]/20 text-[#ff4500]' : 'bg-[#38bdf8]/20 text-[#38bdf8]'}`}>
      {msg.role === 'user' ? 'U' : '🤖'}
    </div>
    <div className={`flex-1 max-w-[85%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
      <div className={`px-4 py-3 rounded-2xl text-xs leading-relaxed
        ${msg.role === 'user'
          ? 'bg-[#ff4500]/15 text-white border border-[#ff4500]/20'
          : 'bg-[#17171c] text-[#c8c7d4] border border-[#22222e]'}`}
        style={{ fontFamily: 'DM Mono, monospace' }}>
        {msg.content}
      </div>
      {msg.filesUpdated > 0 && (
        <span className="text-[#4ade80] text-[10px] px-2" style={{ fontFamily: 'DM Mono, monospace' }}>
          ✓ {msg.filesUpdated} file{msg.filesUpdated > 1 ? 's' : ''} updated
        </span>
      )}
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const VibeCodingWindow = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { solution, problem, generatedPrompt, sessionId: initialSessionId, vibeBackend } = location.state || {};
  const backend = vibeBackend || VIBE_BACKEND;

  // Core state
  const [activeTab,      setActiveTab]      = useState('preview');
  const [activeFile,     setActiveFile]     = useState(0);
  const [deviceMode,     setDeviceMode]     = useState('desktop');
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [sessionId,      setSessionId]      = useState(initialSessionId || null);
  const [appName,        setAppName]        = useState('');
  const [wowFactor,      setWowFactor]      = useState('');

  // Generation state
  const [isGenerating,    setIsGenerating]    = useState(false);
  const [generationPhase, setGenerationPhase] = useState('idle'); // idle | prompt | generating | complete
  const [streamBuffer,    setStreamBuffer]    = useState('');
  const [prompt,          setPrompt]          = useState(generatedPrompt || '');
  const [promptTyped,     setPromptTyped]     = useState(false);

  // Chat state
  const [messages,     setMessages]     = useState([]);
  const [chatInput,    setChatInput]    = useState('');
  const [isChatting,   setIsChatting]   = useState(false);
  const [isIterating,  setIsIterating]  = useState(false);

  // UI state
  const [showPromptPanel, setShowPromptPanel] = useState(true);
  const [tokenCount,      setTokenCount]      = useState(0);
  const [generationTime,  setGenerationTime]  = useState(0);

  const chatEndRef = useRef(null);
  const genStartTime = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!solution || !problem) navigate('/dashboard');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-generate on mount if we have a prompt
  useEffect(() => {
    if (generatedPrompt && !generatedFiles.length) {
      const t = setTimeout(() => {
        setPromptTyped(true);
      }, 500);
      return () => clearTimeout(t);
    }
  }, [generatedPrompt, generatedFiles.length]);

  // When prompt typing is done, trigger generation
  useEffect(() => {
    if (promptTyped && !generatedFiles.length && !isGenerating) {
      const t = setTimeout(() => startGeneration(), 800);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptTyped, generatedFiles.length, isGenerating]);

  const startTimerCount = () => {
    genStartTime.current = Date.now();
    timerRef.current = setInterval(() => {
      setGenerationTime(Math.floor((Date.now() - genStartTime.current) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
  };

  // ── Main generation function ─────────────────────────────────────────────
  const startGeneration = useCallback(async () => {
    if (isGenerating || !prompt) return;

    setIsGenerating(true);
    setGenerationPhase('generating');
    setStreamBuffer('');
    setTokenCount(0);
    startTimerCount();

    // Add system message to chat
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `🚀 Starting generation for: "${solution?.name || 'your app'}"\n\nUsing Nova v2 Lite to build your complete application...`,
      timestamp: Date.now(),
    }]);

    try {
      const res = await fetch(`${backend}/api/vibe/generate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id:      sessionId,
          prompt:          prompt,
          solution_title:  solution?.name || solution?.title || '',
          problem_summary: problem?.nova?.problem_summary || '',
          category:        problem?.reddit?.category || '',
        }),
      });

      if (!res.ok) throw new Error(`Generation failed: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullBuffer = '';
      let result = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'start') {
              setSessionId(data.session_id);
            } else if (data.type === 'token') {
              fullBuffer += data.text;
              setStreamBuffer(prev => prev + data.text);
              setTokenCount(prev => prev + data.text.split(' ').length);
            } else if (data.type === 'complete') {
              result = data.result;
              setSessionId(data.session_id);
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (parseErr) {
            // skip malformed SSE lines
          }
        }
      }

      // ── If complete event gave us result, use it ───────────────────────────
      // ── Otherwise extract directly from the raw stream buffer ─────────────
      if (!result || !result.files?.length) {
        const clean = fullBuffer
          .replace(/```json/g, '').replace(/```html/g, '').replace(/```/g, '')
          .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
          .trim();

        // Case 1: raw HTML in the buffer
        if (clean.includes('<!DOCTYPE html') || clean.includes('<html')) {
          const htmlStart = clean.indexOf('<!DOCTYPE html') !== -1
            ? clean.indexOf('<!DOCTYPE html')
            : clean.indexOf('<html');
          const html = clean.slice(htmlStart);
          const titleMatch = html.match(/<title>([^<]+)<\/title>/i);
          result = {
            app_name:   titleMatch?.[1] || solution?.name || 'App',
            tagline:    '',
            tech_stack: ['React', 'Tailwind'],
            files: [{
              name:        'index.html',
              language:    'html',
              description: 'Generated app',
              content:     html,
            }],
            wow_factor: '',
          };
        }
        // Case 2: JSON in the buffer
        else {
          try {
            const jsonStart = clean.indexOf('{');
            const jsonEnd   = clean.lastIndexOf('}');
            if (jsonStart !== -1 && jsonEnd > jsonStart) {
              result = JSON.parse(clean.slice(jsonStart, jsonEnd + 1));
            }
          } catch {
            // JSON parse failed too
          }
        }
      }

      if (result?.files?.length) {
        setGeneratedFiles(result.files);
        setAppName(result.app_name || solution?.name || '');
        setWowFactor(result.wow_factor || '');
        setGenerationPhase('complete');
        setActiveTab('preview');
        setShowPromptPanel(false);
        stopTimer();

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ Generated ${result.files.length} files successfully!\n\n🌟 Wow factor: ${result.wow_factor || 'Rich interactive UI with live data'}\n\nYou can now see the live preview → and chat below to iterate on the code.`,
          filesUpdated: result.files.length,
          timestamp: Date.now(),
        }]);
      } else {
        console.error('Buffer preview:', fullBuffer.slice(0, 500));
        throw new Error('No files in generation result — check backend logs');
      }

    } catch (err) {
      console.error('Generation error:', err);
      setGenerationPhase('idle');
      stopTimer();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Generation failed: ${err.message}\n\nTry rephrasing your prompt or check the backend connection.`,
        timestamp: Date.now(),
      }]);
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, prompt, solution, problem, sessionId, backend]);

  // ── Chat iteration ───────────────────────────────────────────────────────
  const handleChatSend = async () => {
    if (!chatInput.trim() || isIterating || !generatedFiles.length) return;

    const userMsg = chatInput.trim();
    setChatInput('');
    setIsIterating(true);

    setMessages(prev => [...prev, {
      role: 'user',
      content: userMsg,
      timestamp: Date.now(),
    }]);

    try {
      const res = await fetch(`${backend}/api/vibe/iterate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id:    sessionId,
          user_message:  userMsg,
          current_files: generatedFiles,
        }),
      });

      if (!res.ok) throw new Error(`Iteration failed: ${res.status}`);
      const data = await res.json();

      if (data.files?.length) {
        // Merge updated files
        setGeneratedFiles(prev => {
          const updated = [...prev];
          for (const newFile of data.files) {
            const idx = updated.findIndex(f => f.name === newFile.name);
            if (idx >= 0) updated[idx] = newFile;
            else updated.push(newFile);
          }
          return updated;
        });
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message || 'Updated as requested!',
        filesUpdated: data.files?.length || 0,
        timestamp: Date.now(),
      }]);

    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ ${err.message}`,
        timestamp: Date.now(),
      }]);
    } finally {
      setIsIterating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (generatedFiles.length > 0) handleChatSend();
    }
  };

  if (!solution || !problem) return null;

  const categoryColor = {
    'Finance': '#22d3ee', 'Mental Health': '#a78bfa', 'Career': '#fb923c',
    'Health': '#4ade80', 'Tech': '#60a5fa', 'Relationships': '#f472b6',
    'Education': '#facc15', 'Home & Life': '#a3e635', 'Other': '#7b7a88',
  }[problem?.reddit?.category] || '#ff4500';

  const currentFile = generatedFiles[activeFile];
  const isComplete = generationPhase === 'complete';
  const isIdle = generationPhase === 'idle' && !isGenerating;

  return (
    <div className="h-screen bg-[#09090e] text-[#f0eff6] flex flex-col overflow-hidden">

      {/* ══ TOP BAR ══ */}
      <header className="flex-shrink-0 h-14 bg-[#09090e] border-b border-[#22222e] flex items-center px-4 gap-4 z-50">
        {/* Left */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button onClick={() => navigate(-1)}
            className="text-[#7b7a88] hover:text-white transition-colors text-xs flex-shrink-0"
            style={{ fontFamily: 'DM Mono, monospace' }}>
            ←
          </button>
          <div className="w-px h-5 bg-[#22222e]" />
          <span className="text-[#ff4500] text-lg">⬤</span>
          <span className="font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>PainScout</span>
          <span className="text-[#3a3a48]">›</span>
          <span className="text-[#7b7a88] text-xs truncate max-w-[120px]"
            style={{ fontFamily: 'DM Mono, monospace' }}>
            {trunc(problem?.reddit?.title || '', 30)}
          </span>
          <span className="text-[#3a3a48]">›</span>
          <span className="text-xs font-bold truncate max-w-[160px]"
            style={{ color: categoryColor, fontFamily: 'DM Mono, monospace' }}>
            {trunc(solution?.name || solution?.title || '', 40)}
          </span>
        </div>

        {/* Center tabs */}
        <div className="flex items-center bg-[#17171c] rounded-lg p-1 gap-0.5 flex-shrink-0">
          {[
            { key: 'preview', label: '▶ Preview' },
            { key: 'code',    label: '{ } Code'  },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-1.5 rounded text-xs font-bold transition-all ${
                activeTab === tab.key
                  ? 'bg-[#ff4500] text-white'
                  : 'text-[#7b7a88] hover:text-white'
              }`}
              style={{ fontFamily: 'DM Mono, monospace' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Right */}
        <div className="flex items-center gap-3 flex-1 justify-end">
          {/* Status */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isGenerating ? 'bg-[#fbbf24] animate-pulse' : isComplete ? 'bg-[#4ade80]' : 'bg-[#3a3a48]'}`} />
            <span className="text-xs" style={{ fontFamily: 'DM Mono, monospace',
              color: isGenerating ? '#fbbf24' : isComplete ? '#4ade80' : '#7b7a88' }}>
              {isGenerating ? `Generating… ${generationTime}s` : isComplete ? `Done · ${generatedFiles.length} files` : 'Nova ready'}
            </span>
          </div>

          {isGenerating && (
            <div className="text-xs text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
              ~{tokenCount} tokens
            </div>
          )}

          {/* Device toggle */}
          {isComplete && (
            <div className="flex items-center bg-[#17171c] rounded-lg p-1 gap-0.5">
              {[
                { key: 'desktop', icon: '🖥' },
                { key: 'tablet',  icon: '📱' },
                { key: 'mobile',  icon: '📲' },
              ].map(d => (
                <button key={d.key}
                  onClick={() => setDeviceMode(d.key)}
                  className={`px-2 py-1 rounded text-sm transition-all ${deviceMode === d.key ? 'bg-[#22222e]' : 'opacity-50 hover:opacity-75'}`}>
                  {d.icon}
                </button>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <button className="px-3 py-1.5 border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white rounded text-xs transition-all"
            style={{ fontFamily: 'DM Mono, monospace' }}>
            ☁ Deploy
          </button>
          {isComplete && (
            <button className="px-3 py-1.5 border border-[#22222e] text-[#7b7a88] hover:border-white hover:text-white rounded text-xs transition-all"
              style={{ fontFamily: 'DM Mono, monospace' }}>
              ⬇ ZIP
            </button>
          )}
        </div>
      </header>

      {/* ══ MAIN LAYOUT — 3 panels ══ */}
      <div className="flex flex-1 overflow-hidden">

        {/* ═══ PANEL 1: Chat + Prompt ═══ */}
        <div className="w-[340px] flex-shrink-0 flex flex-col border-r border-[#22222e] bg-[#0d0d12]">

          {/* Prompt section */}
          {showPromptPanel && (
            <div className="flex-shrink-0 border-b border-[#22222e]">
              {/* Prompt header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#22222e]/50">
                <div className="flex items-center gap-2">
                  <span className="text-[#ff4500] text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>⚡ NOVA PROMPT</span>
                  {!promptTyped && prompt && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] animate-pulse" />
                  )}
                  {promptTyped && (
                    <span className="text-[#4ade80] text-[10px]" style={{ fontFamily: 'DM Mono, monospace' }}>ready</span>
                  )}
                </div>
                <button onClick={() => setShowPromptPanel(false)}
                  className="text-[#3a3a48] hover:text-[#7b7a88] text-xs">×</button>
              </div>

              {/* Prompt text with typing animation */}
              <div className="p-4 max-h-48 overflow-y-auto">
                {prompt ? (
                  <p className="text-[#c8c7d4] text-xs leading-relaxed"
                    style={{ fontFamily: 'DM Mono, monospace' }}>
                    {!promptTyped
                      ? <TypingText text={prompt} speed={12} onDone={() => setPromptTyped(true)} />
                      : prompt
                    }
                  </p>
                ) : (
                  <p className="text-[#3a3a48] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                    No prompt generated yet…
                  </p>
                )}
              </div>

              {/* Prompt actions */}
              {promptTyped && !isGenerating && generationPhase !== 'complete' && (
                <div className="px-4 pb-4">
                  <button
                    onClick={startGeneration}
                    className="w-full py-2.5 bg-[#ff4500] hover:bg-[#e03d00] text-white rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 group"
                    style={{ fontFamily: 'Syne, sans-serif' }}>
                    <span className="group-hover:animate-bounce">🚀</span>
                    Generate App with Nova
                  </button>
                </div>
              )}

              {/* Generation progress */}
              {isGenerating && (
                <div className="px-4 pb-4 space-y-2">
                  <div className="flex items-center justify-between text-xs text-[#7b7a88]"
                    style={{ fontFamily: 'DM Mono, monospace' }}>
                    <span>Generating with Nova v2 Lite…</span>
                    <span>{generationTime}s</span>
                  </div>
                  <div className="h-1.5 bg-[#22222e] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#ff4500] to-[#fbbf24] rounded-full animate-pulse"
                      style={{ width: '60%', animation: 'progress 2s ease-in-out infinite' }} />
                  </div>
                  <p className="text-[#3a3a48] text-[10px]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    Writing React components · Styling UI · Adding mock data
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Show prompt toggle */}
          {!showPromptPanel && (
            <button
              onClick={() => setShowPromptPanel(true)}
              className="flex-shrink-0 w-full py-2 text-xs text-[#3a3a48] hover:text-[#7b7a88] border-b border-[#22222e] transition-all"
              style={{ fontFamily: 'DM Mono, monospace' }}>
              ↓ Show prompt
            </button>
          )}

          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8 space-y-3">
                <div className="text-4xl">🤖</div>
                <p className="text-[#3a3a48] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                  Nova is ready to build your app
                </p>
                {generatedFiles.length === 0 && promptTyped && (
                  <p className="text-[#ff4500] text-xs animate-pulse" style={{ fontFamily: 'DM Mono, monospace' }}>
                    Starting generation…
                  </p>
                )}
              </div>
            )}
            {messages.map((msg, i) => (
              <ChatMessage key={i} msg={msg} />
            ))}
            {(isChatting || isIterating) && (
              <div className="flex gap-2 items-center text-[#38bdf8] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                <div className="flex gap-1">
                  {[0,1,2].map(j => (
                    <div key={j} className="w-1.5 h-1.5 bg-[#38bdf8] rounded-full animate-bounce"
                      style={{ animationDelay: `${j * 150}ms` }} />
                  ))}
                </div>
                <span>Nova is iterating…</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat input */}
          <div className="flex-shrink-0 p-3 border-t border-[#22222e]">
            {!isComplete ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#17171c] border border-[#22222e] rounded-xl opacity-50">
                <span className="text-[#3a3a48] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                  Chat available after generation
                </span>
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask Nova to modify the app… (Enter to send)"
                  rows={2}
                  disabled={isIterating}
                  className="flex-1 bg-[#17171c] border border-[#22222e] hover:border-[#ff4500]/40 focus:border-[#ff4500] rounded-xl px-3 py-2.5 text-xs text-[#c8c7d4] resize-none outline-none placeholder-[#3a3a48] transition-all disabled:opacity-50"
                  style={{ fontFamily: 'DM Mono, monospace' }}
                />
                <button
                  onClick={handleChatSend}
                  disabled={!chatInput.trim() || isIterating}
                  className="flex-shrink-0 w-9 h-9 bg-[#ff4500] hover:bg-[#e03d00] disabled:opacity-30 rounded-xl flex items-center justify-center transition-all">
                  <span className="text-white text-sm">↑</span>
                </button>
              </div>
            )}

            {/* Quick actions */}
            {isComplete && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[
                  'Add dark mode toggle',
                  'Make it mobile-friendly',
                  'Add more sample data',
                  'Improve animations',
                ].map(q => (
                  <button key={q}
                    onClick={() => { setChatInput(q); }}
                    className="px-2 py-1 bg-[#17171c] border border-[#22222e] hover:border-[#ff4500]/40 rounded-full text-[10px] text-[#7b7a88] hover:text-white transition-all"
                    style={{ fontFamily: 'DM Mono, monospace' }}>
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ═══ PANEL 2: Preview / Code ═══ */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {activeTab === 'preview' && (
            <>
              {/* App name bar */}
              {isComplete && appName && (
                <div className="flex-shrink-0 flex items-center gap-3 px-5 py-2.5 bg-[#17171c] border-b border-[#22222e]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#4ade80]" />
                  <span className="font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{appName}</span>
                  {wowFactor && (
                    <>
                      <span className="text-[#3a3a48]">·</span>
                      <span className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                        ✨ {trunc(wowFactor, 60)}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Preview frame */}
              {isGenerating ? (
                <div className="flex-1 flex items-center justify-center bg-[#0d0d12]">
                  <div className="text-center space-y-6 max-w-sm px-8">
                    {/* Animated code stream preview */}
                    <div className="w-full h-32 bg-[#17171c] rounded-xl border border-[#22222e] overflow-hidden relative">
                      <div className="absolute inset-0 p-3">
                        <div className="space-y-1.5 opacity-70">
                          {streamBuffer.split('\n').slice(-8).map((line, i) => (
                            <div key={i} className="h-2.5 rounded"
                              style={{
                                background: `rgba(255,69,0,${0.1 + (i / 8) * 0.3})`,
                                width: `${30 + (line.length % 60)}%`,
                              }} />
                          ))}
                        </div>
                        <div className="absolute bottom-2 right-2 text-[10px] text-[#ff4500] font-bold animate-pulse"
                          style={{ fontFamily: 'DM Mono, monospace' }}>
                          WRITING CODE…
                        </div>
                      </div>
                    </div>

                    {/* Spinner */}
                    <div className="relative w-16 h-16 mx-auto">
                      <svg className="w-16 h-16 animate-spin" viewBox="0 0 64 64">
                        <circle cx="32" cy="32" r="28" stroke="#22222e" strokeWidth="4" fill="none" />
                        <circle cx="32" cy="32" r="28" stroke="#ff4500" strokeWidth="4" fill="none"
                          strokeDasharray="44 132" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-xl">🤖</div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-white font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                        Nova is coding your app
                      </p>
                      <p className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                        {generationTime}s elapsed · ~{tokenCount} tokens generated
                      </p>
                    </div>

                    {/* Phase indicators */}
                    <div className="flex justify-center gap-4">
                      {['Architecture', 'Components', 'Styling', 'Data'].map((phase, i) => (
                        <div key={phase} className="text-center">
                          <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${
                            tokenCount > i * 100
                              ? 'bg-[#4ade80]'
                              : tokenCount > i * 50
                              ? 'bg-[#fbbf24] animate-pulse'
                              : 'bg-[#22222e]'
                          }`} />
                          <p className="text-[9px] text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>{phase}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <LivePreviewFrame files={generatedFiles} deviceMode={deviceMode} />
              )}
            </>
          )}

          {activeTab === 'code' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* File tabs */}
              <div className="flex-shrink-0 flex items-center gap-0 border-b border-[#22222e] bg-[#0d0d12] overflow-x-auto">
                {generatedFiles.length > 0 ? (
                  generatedFiles.map((file, i) => (
                    <FileTab key={i} file={file} active={activeFile === i} onClick={() => setActiveFile(i)} />
                  ))
                ) : (
                  <div className="px-4 py-2 text-xs text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    No files yet
                  </div>
                )}
              </div>

              {/* Code content */}
              <div className="flex-1 overflow-hidden bg-[#0d0d12]">
                {currentFile ? (
                  isGenerating ? (
                    <div className="h-full overflow-auto p-4">
                      <pre className="text-xs text-[#4ade80]/70 leading-relaxed"
                        style={{ fontFamily: 'DM Mono, monospace' }}>
                        {streamBuffer}
                        <span className="inline-block w-2 h-4 bg-[#ff4500] ml-1 animate-pulse" />
                      </pre>
                    </div>
                  ) : (
                    <CodeBlock code={currentFile.content} language={currentFile.language} />
                  )
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-[#3a3a48] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                      {isGenerating ? 'Generating…' : 'Generate code to see files'}
                    </p>
                  </div>
                )}
              </div>

              {/* File stats bar */}
              {currentFile && (
                <div className="flex-shrink-0 flex items-center gap-4 px-4 py-2 border-t border-[#22222e] bg-[#0d0d12]">
                  <span className="text-[10px] text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    {currentFile.name}
                  </span>
                  <span className="text-[10px] text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    {currentFile.content?.split('\n').length || 0} lines
                  </span>
                  <span className="text-[10px] text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    {currentFile.language?.toUpperCase()}
                  </span>
                  <button
                    onClick={() => navigator.clipboard.writeText(currentFile.content)}
                    className="ml-auto text-[10px] text-[#7b7a88] hover:text-white transition-colors px-2 py-0.5 border border-[#22222e] hover:border-[#7b7a88] rounded"
                    style={{ fontFamily: 'DM Mono, monospace' }}>
                    Copy
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ PANEL 3: Context / Info ═══ */}
        <div className="w-[260px] flex-shrink-0 border-l border-[#22222e] bg-[#0d0d12] flex flex-col overflow-y-auto">

          {/* Solution context */}
          <div className="p-4 border-b border-[#22222e]">
            <p className="text-[10px] font-bold text-[#7b7a88] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
              BUILDING
            </p>
            <p className="text-sm font-bold text-white leading-tight mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
              {solution?.name || solution?.title}
            </p>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold"
              style={{ fontFamily: 'DM Mono, monospace',
                backgroundColor: `${categoryColor}22`, color: categoryColor }}>
              {problem?.reddit?.category}
            </span>
          </div>

          {/* Stats */}
          {isComplete && (
            <div className="p-4 border-b border-[#22222e] space-y-3">
              <p className="text-[10px] font-bold text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                GENERATION STATS
              </p>
              <div className="space-y-2">
                {[
                  { label: 'Files', value: generatedFiles.length, color: '#38bdf8' },
                  { label: 'Lines', value: generatedFiles.reduce((a, f) => a + (f.content?.split('\n').length || 0), 0), color: '#4ade80' },
                  { label: 'Time', value: `${generationTime}s`, color: '#fbbf24' },
                  { label: 'Model', value: 'Nova v2 Lite', color: '#a78bfa' },
                ].map(stat => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <span className="text-[10px] text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                      {stat.label}
                    </span>
                    <span className="text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace', color: stat.color }}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files list */}
          {generatedFiles.length > 0 && (
            <div className="p-4 border-b border-[#22222e]">
              <p className="text-[10px] font-bold text-[#7b7a88] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                FILES ({generatedFiles.length})
              </p>
              <div className="space-y-1.5">
                {generatedFiles.map((file, i) => {
                  const ext = file.name.split('.').pop();
                  const colors = { jsx: '#38bdf8', html: '#fb923c', css: '#a78bfa', js: '#fbbf24' };
                  return (
                    <button key={i}
                      onClick={() => { setActiveTab('code'); setActiveFile(i); }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#17171c] transition-all text-left">
                      <span className="text-[10px] font-bold" style={{ color: colors[ext] || '#7b7a88', fontFamily: 'DM Mono, monospace' }}>
                        {ext.toUpperCase()}
                      </span>
                      <span className="text-xs text-[#c8c7d4]" style={{ fontFamily: 'DM Mono, monospace' }}>
                        {file.name}
                      </span>
                      <span className="text-[10px] text-[#3a3a48] ml-auto" style={{ fontFamily: 'DM Mono, monospace' }}>
                        {file.content?.split('\n').length}L
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Problem context */}
          <div className="p-4 border-b border-[#22222e]">
            <p className="text-[10px] font-bold text-[#7b7a88] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
              SOLVING
            </p>
            <p className="text-xs text-[#7b7a88] leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>
              {trunc(problem?.nova?.problem_summary || '', 120)}
            </p>
          </div>

          {/* Pain points */}
          {problem?.nova?.key_pain_points?.length > 0 && (
            <div className="p-4">
              <p className="text-[10px] font-bold text-[#ef4444] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                PAIN POINTS
              </p>
              <ul className="space-y-1.5">
                {problem.nova.key_pain_points.slice(0, 3).map((p, i) => (
                  <li key={i} className="flex gap-2 text-[10px] text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                    <span className="text-[#ef4444] flex-shrink-0">✗</span>
                    <span>{trunc(p, 55)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

      </div>

      <style>{`
        @keyframes progress {
          0%   { width: 20%; opacity: 0.8; }
          50%  { width: 80%; opacity: 1;   }
          100% { width: 20%; opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default VibeCodingWindow;